import { useEffect, useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, CircularProgress, Checkbox, FormControlLabel, Autocomplete,
} from '@mui/material'
import { Add, MoreVert, Edit, Send, AttachMoney, PlaylistAddCheck } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listAssetPurchaseRequests, getAssetPurchaseRequest, createAssetPurchaseRequest, updateAssetPurchaseRequest, submitAssetPurchaseRequest,
  listAssetsInProgress, createAssetFromInvoice, addAssetCostComponent, commissionAsset,
  ASSET_CATEGORIES, COST_COMPONENT_TYPES, DEPRECIATION_METHODS,
  type AssetPurchaseRequestListItem, type AssetPurchaseRequestStatus, type SaveAssetPurchaseRequestPayload,
  type AssetListItem, type AssetCategory, type AssetCostComponentType, type AssetDepreciationMethod, type CreateAssetFromInvoicePayload,
} from '../../api/active'
import { COST_CENTERS, type CostCenter } from '../../api/deconturi'
import { listSupplierInvoices, getSupplierInvoice, type SupplierInvoiceListItem } from '../../api/facturiFurnizor'
import { listUsers } from '../../api/admin'
import { errorMessage } from '../../api/client'

const PR_STATUS_LABEL: Record<AssetPurchaseRequestStatus, string> = {
  Draft: 'Ciornă', PendingApproval: 'În aprobare', Approved: 'Aprobată', Rejected: 'Respinsă',
}
const PR_STATUS_COLOR: Record<AssetPurchaseRequestStatus, 'default' | 'info' | 'success' | 'error'> = {
  Draft: 'default', PendingApproval: 'info', Approved: 'success', Rejected: 'error',
}
const CATEGORY_LABEL = Object.fromEntries(ASSET_CATEGORIES.map((c) => [c.value, c.label])) as Record<AssetCategory, string>
const COST_CENTER_LABEL = Object.fromEntries(COST_CENTERS.map((c) => [c.value, c.label])) as Record<CostCenter, string>

export default function IntrariActivePage() {
  const [tab, setTab] = useState(0)
  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Active fixe</Typography>
        <Typography variant="inherit" color="text.primary">Intrări</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Intrări de active fixe</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Cereri de achiziție" />
        <Tab label="Active în curs de punere în funcțiune" />
      </Tabs>
      {tab === 0 ? <PurchaseRequestsTab /> : <InProgressTab />}
    </Stack>
  )
}

// ---------- Tab 1: cereri de achiziție de imobilizare (FR-FAF-013) ----------

const EMPTY_PR: SaveAssetPurchaseRequestPayload = {
  date: new Date().toISOString().slice(0, 10), name: '', category: 'IT', costCenter: 'Administrativ',
  estimatedValue: 0, currency: 'RON', justification: '', notes: null,
}

function PurchaseRequestsTab() {
  const [formTarget, setFormTarget] = useState<AssetPurchaseRequestListItem | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: AssetPurchaseRequestListItem } | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['active', 'intrari', 'cereri'], queryFn: listAssetPurchaseRequests })
  const closeMenu = () => setMenuAnchor(null)

  const submitMutation = useMutation({
    mutationFn: submitAssetPurchaseRequest,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['active', 'intrari', 'cereri'] }),
  })

  const columns = useMemo<GridColDef<AssetPurchaseRequestListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 140, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'date', headerName: 'Dată', width: 110 },
    { field: 'name', headerName: 'Activ propus', flex: 1, minWidth: 200 },
    { field: 'category', headerName: 'Categorie', width: 170, renderCell: (p) => CATEGORY_LABEL[p.row.category] },
    { field: 'costCenter', headerName: 'Centru de cost', width: 140, renderCell: (p) => COST_CENTER_LABEL[p.row.costCenter] },
    { field: 'estimatedValue', headerName: 'Valoare estimată', width: 150, type: 'number', renderCell: (p) => `${p.row.estimatedValue.toFixed(2).replace('.', ',')} ${p.row.currency}` },
    { field: 'requestedByUserName', headerName: 'Solicitant', width: 160 },
    {
      field: 'status', headerName: 'Status', width: 130, sortable: false,
      renderCell: (p) => <Chip size="small" label={PR_STATUS_LABEL[p.row.status]} color={PR_STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Cerere nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <PurchaseRequestFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" disabled={menuAnchor.row.status !== 'Draft'} onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="submit" disabled={menuAnchor.row.status !== 'Draft'}
            onClick={() => { submitMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Send fontSize="small" /></ListItemIcon><ListItemText>Trimite spre aprobare</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </Stack>
  )
}

function PurchaseRequestFormDialog({ target, onClose }: { target: AssetPurchaseRequestListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()
  const [form, setForm] = useState<SaveAssetPurchaseRequestPayload>(EMPTY_PR)

  const { data: detail } = useQuery({
    queryKey: ['active', 'intrari', 'cerere', editing ? (target as AssetPurchaseRequestListItem).id : null],
    queryFn: () => getAssetPurchaseRequest((target as AssetPurchaseRequestListItem).id), enabled: editing,
  })

  const mutation = useMutation({
    mutationFn: (payload: SaveAssetPurchaseRequestPayload) => editing
      ? updateAssetPurchaseRequest((target as AssetPurchaseRequestListItem).id, payload).then(() => undefined)
      : createAssetPurchaseRequest(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['active', 'intrari', 'cereri'] })
      setForm(EMPTY_PR)
      onClose()
    },
  })

  useEffect(() => {
    if (target === 'new') setForm(EMPTY_PR)
  }, [target])

  const set = <K extends keyof SaveAssetPurchaseRequestPayload>(key: K, value: SaveAssetPurchaseRequestPayload[K]) => setForm((f) => ({ ...f, [key]: value }))

  const f = editing && detail
    ? { date: detail.date, name: detail.name, category: detail.category, costCenter: detail.costCenter, estimatedValue: detail.estimatedValue, currency: detail.currency, justification: detail.justification, notes: detail.notes }
    : target === 'new' ? form : null

  if (editing && !detail) {
    return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth><DialogContent><Stack sx={{ py: 4, alignItems: 'center' }}><CircularProgress /></Stack></DialogContent></Dialog>
  }
  if (!open || !f) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Cerere ${detail?.number}` : 'Cerere de achiziție de imobilizare'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField type="date" label="Dată" value={f.date} onChange={(e) => set('date', e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Denumire activ propus" value={f.name} onChange={(e) => set('name', e.target.value)} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField select label="Categorie" value={f.category} onChange={(e) => set('category', e.target.value as AssetCategory)} fullWidth>
              {ASSET_CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField select label="Centru de cost" value={f.costCenter} onChange={(e) => set('costCenter', e.target.value as CostCenter)} fullWidth>
              {COST_CENTERS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField type="number" label="Valoare estimată" value={f.estimatedValue} onChange={(e) => set('estimatedValue', Number(e.target.value))} fullWidth />
            <TextField label="Monedă" value={f.currency} onChange={(e) => set('currency', e.target.value)} fullWidth />
          </Stack>
          <TextField label="Justificarea investiției" value={f.justification} onChange={(e) => set('justification', e.target.value)} fullWidth multiline minRows={3} required />
          <TextField label="Note" value={f.notes ?? ''} onChange={(e) => set('notes', e.target.value || null)} fullWidth multiline minRows={2} />
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={!f.name || !f.justification || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => mutation.mutate(f)}>
          Salvează
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---------- Tab 2: active în curs de punere în funcțiune (FR-FAF-014…016) ----------

function InProgressTab() {
  const [fromInvoiceOpen, setFromInvoiceOpen] = useState(false)
  const [costTarget, setCostTarget] = useState<AssetListItem | null>(null)
  const [commissionTarget, setCommissionTarget] = useState<AssetListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: AssetListItem } | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['active', 'intrari', 'in-curs'], queryFn: listAssetsInProgress })
  const closeMenu = () => setMenuAnchor(null)

  const columns = useMemo<GridColDef<AssetListItem>[]>(() => [
    { field: 'inventoryNumber', headerName: 'Nr. inventar', width: 150, renderCell: (p) => <code>{p.row.inventoryNumber}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    { field: 'category', headerName: 'Categorie', width: 180, renderCell: (p) => CATEGORY_LABEL[p.row.category] },
    { field: 'acquisitionDate', headerName: 'Dată achiziție', width: 130 },
    { field: 'entryValue', headerName: 'Valoare acumulată', width: 150, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFromInvoiceOpen(true)}>Generează activ din factură</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <CreateFromInvoiceDialog open={fromInvoiceOpen} onClose={() => setFromInvoiceOpen(false)} />
      <AddCostComponentDialog target={costTarget} onClose={() => setCostTarget(null)} />
      <CommissionDialog target={commissionTarget} onClose={() => setCommissionTarget(null)} />
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="cost" onClick={() => { setCostTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><AttachMoney fontSize="small" /></ListItemIcon><ListItemText>Adaugă cost suplimentar</ListItemText>
          </MenuItem>,
          <MenuItem key="commission" onClick={() => { setCommissionTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><PlaylistAddCheck fontSize="small" /></ListItemIcon><ListItemText>Pune în funcțiune</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </Stack>
  )
}

const EMPTY_FROM_INVOICE: Omit<CreateAssetFromInvoicePayload, 'supplierInvoiceId' | 'supplierInvoiceLineIds'> = {
  name: '', category: 'IT', subcategory: null, depreciationDurationMonths: 36, depreciationMethod: 'Liniara',
  residualValue: 0, location: null, responsibleUserId: null, costCenter: 'Administrativ', serialNumber: null,
  warrantyUntil: null, notes: null, assetPurchaseRequestId: null,
}

function CreateFromInvoiceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [invoice, setInvoice] = useState<SupplierInvoiceListItem | null>(null)
  const [lineIds, setLineIds] = useState<string[]>([])
  const [form, setForm] = useState(EMPTY_FROM_INVOICE)

  const { data: invoices } = useQuery({ queryKey: ['achizitii', 'facturi'], queryFn: () => listSupplierInvoices(), enabled: open })
  const approvedInvoices = (invoices ?? []).filter((i) => i.status === 'Approved')
  const { data: invoiceDetail } = useQuery({
    queryKey: ['achizitii', 'factura', invoice?.id ?? null], queryFn: () => getSupplierInvoice(invoice!.id), enabled: !!invoice,
  })
  const { data: purchaseRequests } = useQuery({ queryKey: ['active', 'intrari', 'cereri'], queryFn: listAssetPurchaseRequests, enabled: open })
  const approvedRequests = (purchaseRequests ?? []).filter((r) => r.status === 'Approved')
  const { data: users } = useQuery({ queryKey: ['admin', 'utilizatori'], queryFn: () => listUsers(), enabled: open })

  const selectedLines = (invoiceDetail?.lines ?? []).filter((l) => l.id && lineIds.includes(l.id))
  const total = selectedLines.reduce((sum, l) => sum + l.netAmount, 0)

  const reset = () => { setInvoice(null); setLineIds([]); setForm(EMPTY_FROM_INVOICE) }

  const mutation = useMutation({
    mutationFn: () => createAssetFromInvoice({ supplierInvoiceId: invoice!.id, supplierInvoiceLineIds: lineIds, ...form }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['active', 'intrari', 'in-curs'] })
      void qc.invalidateQueries({ queryKey: ['active', 'registru'] })
      reset()
      onClose()
    },
  })

  const set = <K extends keyof typeof EMPTY_FROM_INVOICE>(key: K, value: (typeof EMPTY_FROM_INVOICE)[K]) => setForm((f) => ({ ...f, [key]: value }))

  return (
    <Dialog open={open} onClose={() => { reset(); onClose() }} maxWidth="sm" fullWidth>
      <DialogTitle>Generează activ din factură furnizor</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Autocomplete
            options={approvedInvoices} getOptionLabel={(i) => `${i.number} — ${i.supplierName} (${i.total.toFixed(2).replace('.', ',')} ${i.currency})`}
            value={invoice} onChange={(_, v) => { setInvoice(v); setLineIds([]) }}
            renderInput={(params) => <TextField {...params} label="Factură furnizor aprobată" />}
          />
          {invoiceDetail && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Selectează liniile care compun activul</Typography>
              {invoiceDetail.lines.map((l) => (
                <FormControlLabel key={l.id} control={
                  <Checkbox checked={!!l.id && lineIds.includes(l.id)}
                    onChange={(e) => setLineIds((ids) => e.target.checked ? [...ids, l.id!] : ids.filter((id) => id !== l.id))} />
                } label={`${l.description} — ${l.netAmount.toFixed(2).replace('.', ',')}`} />
              ))}
              {lineIds.length > 0 && <Alert severity="info">Valoare de intrare inițială: {total.toFixed(2).replace('.', ',')}</Alert>}
            </Stack>
          )}
          <TextField label="Denumire activ" value={form.name} onChange={(e) => set('name', e.target.value)} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField select label="Categorie" value={form.category} onChange={(e) => set('category', e.target.value as AssetCategory)} fullWidth>
              {ASSET_CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField label="Subcategorie" value={form.subcategory ?? ''} onChange={(e) => set('subcategory', e.target.value || null)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField type="number" label="Durată amortizare (luni)" value={form.depreciationDurationMonths}
              onChange={(e) => set('depreciationDurationMonths', Number(e.target.value))} fullWidth />
            <TextField select label="Metodă amortizare" value={form.depreciationMethod}
              onChange={(e) => set('depreciationMethod', e.target.value as AssetDepreciationMethod)} fullWidth>
              {DEPRECIATION_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <TextField type="number" label="Valoare reziduală" value={form.residualValue} onChange={(e) => set('residualValue', Number(e.target.value))} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Locație" value={form.location ?? ''} onChange={(e) => set('location', e.target.value || null)} fullWidth />
            <TextField select label="Centru de cost" value={form.costCenter} onChange={(e) => set('costCenter', e.target.value as CostCenter)} fullWidth>
              {COST_CENTERS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
          </Stack>
          <Autocomplete
            options={users?.map((u) => u.id) ?? []} getOptionLabel={(id) => { const u = users?.find((x) => x.id === id); return u ? `${u.firstName} ${u.lastName}` : id }}
            value={form.responsibleUserId} onChange={(_, v) => set('responsibleUserId', v)}
            renderInput={(params) => <TextField {...params} label="Responsabil (opțional)" />}
          />
          <Autocomplete
            options={approvedRequests.map((r) => r.id)} getOptionLabel={(id) => approvedRequests.find((r) => r.id === id)?.number ?? id}
            value={form.assetPurchaseRequestId} onChange={(_, v) => set('assetPurchaseRequestId', v)}
            renderInput={(params) => <TextField {...params} label="Cerere de achiziție (opțional)" />}
          />
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={() => { reset(); onClose() }}>Renunță</Button>
        <Button variant="contained" disabled={!invoice || lineIds.length === 0 || !form.name || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => mutation.mutate()}>
          Generează
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function AddCostComponentDialog({ target, onClose }: { target: AssetListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [type, setType] = useState<AssetCostComponentType>('Transport')
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState(0)

  const mutation = useMutation({
    mutationFn: () => addAssetCostComponent(target!.id, type, description, amount, null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['active', 'intrari', 'in-curs'] })
      setDescription(''); setAmount(0); setType('Transport')
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cost suplimentar — {target?.inventoryNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField select label="Tip" value={type} onChange={(e) => setType(e.target.value as AssetCostComponentType)} fullWidth>
            {COST_COMPONENT_TYPES.filter((t) => t.value !== 'Achizitie').map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <TextField label="Descriere" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
          <TextField type="number" label="Sumă" value={amount} onChange={(e) => setAmount(Number(e.target.value))} fullWidth />
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={!description || amount <= 0 || mutation.isPending} onClick={() => mutation.mutate()}>Adaugă</Button>
      </DialogActions>
    </Dialog>
  )
}

function CommissionDialog({ target, onClose }: { target: AssetListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))

  const mutation = useMutation({
    mutationFn: () => commissionAsset(target!.id, date),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['active', 'intrari', 'in-curs'] })
      void qc.invalidateQueries({ queryKey: ['active', 'registru'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Pune în funcțiune — {target?.inventoryNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Valoare de intrare finală: {target?.entryValue.toFixed(2).replace('.', ',')}</Typography>
          <TextField type="date" label="Data punerii în funcțiune" value={date} onChange={(e) => setDate(e.target.value)}
            fullWidth slotProps={{ inputLabel: { shrink: true } }} />
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate()}>Pune în funcțiune</Button>
      </DialogActions>
    </Dialog>
  )
}
